/**
 * Production line — opens browser print with the legacy 100mm×56mm label HTML.
 */

import React from 'react';
import { Printer } from 'lucide-react';
import type { OrderSummary } from '../../utils/types';
import { useLazyListOrderImagesQuery } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import {
  buildProductionLineLabelHtml,
  printProductionLineLabel,
} from '../../utils/productionLinePrintLabel';

export interface PrintLabelButtonCellProps {
  order: OrderSummary;
  stickyStyle?: React.CSSProperties;
}

export const PrintLabelButtonCell: React.FC<PrintLabelButtonCellProps> = ({ order, stickyStyle }) => {
  const toast = useToast();
  const [fetchImages, { isFetching }] = useLazyListOrderImagesQuery();

  const handleClick = async () => {
    const id = String(order.id ?? '').trim();
    let results: Record<string, unknown>[] = [];
    if (id) {
      try {
        const res = await fetchImages({ orderId: id }).unwrap();
        results = (res?.results ?? []) as Record<string, unknown>[];
      } catch {
        toast.error('Print Label', 'Could not load order images. Try again.');
        return;
      }
    }

    const html = buildProductionLineLabelHtml(order as unknown as Record<string, unknown>, results);
    if (!html) {
      toast.info('Print Label', 'Nothing to print for this order.');
      return;
    }

    if (!printProductionLineLabel(html)) {
      toast.error('Print Label', 'Could not start printing. Refresh the page and try again.');
    }
  };

  return (
    <td className="p-2 border text-center" style={stickyStyle}>
      <button
        type="button"
        disabled={isFetching}
        className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
        onClick={() => {
          void handleClick();
        }}
      >
        <Printer className="w-4 h-4 mr-1" />
        {isFetching ? '…' : 'Print'}
      </button>
    </td>
  );
};

PrintLabelButtonCell.displayName = 'PrintLabelButtonCell';
